import { isLeft } from 'fp-ts/Either';
import * as t from 'io-ts';
import { isEmpty, isNaN, isString, pick } from 'lodash';
import { FUNCTIONS_ERROR_CODE_KEYS } from '../types/CallResponse';
import { pascalToSnake } from '../utilities';
import { timestampCodec } from './codecs';
;

// Helper function to obtain keys from io-ts Type
function getKeysFromType(type: t.Any): string[] {
    if (type instanceof t.InterfaceType || type instanceof t.PartialType || type instanceof t.StrictType) {
        return Object.keys(type.props);
    } else if (type instanceof t.IntersectionType) {
        return type.types.flatMap(getKeysFromType);
    } else if (type instanceof t.ExactType) {
        return getKeysFromType(type.type);
    } else if (type instanceof t.ArrayType) {
        return getKeysFromType(type.type);
    } else {
        return [];
    }
}

type CustomError = {
    code: keyof typeof FUNCTIONS_ERROR_CODE_KEYS;
    message: string;
    details: any;
}

function decode<T extends t.Any>(
    data: unknown,
    schema: T,
    options?: {
        sanitize?: boolean;
    }
): t.TypeOf<T> {
    // If sanitize is true, only keep keys that exist in schema
    const cleanData = options && options.sanitize ? pick(data as object, getKeysFromType(schema)) : data;
    const result = schema.decode(cleanData);
    if (isLeft(result)) {
        const lastContext = result.left[result.left.length - 1].context;
        let errorContext: any = lastContext[lastContext.length - 1];
        let parentPath: string[] = [];
        
        for (let i = 0; i < lastContext.length; i++) {
            const currentContext = lastContext[i];
            if (!isEmpty(currentContext.key) && isNaN(parseInt(currentContext.key))) {
                parentPath.push(currentContext.key);
                errorContext = currentContext;
            }
        }

        if (parentPath.length > 0) {
            errorContext.key = parentPath.join('.');
        }

        throw {
            code: "invalid-argument",
            message: `Validation error`,
            details: errorContext
        } as CustomError;
    }
    return result.right;
}

function enumCodec<T extends Record<string, string | number>>(e: T) {
    const isEnumValue = (input: any): input is T[keyof T] => Object.values(e).includes(input);
    return new t.Type<T[keyof T], T[keyof T], unknown>(
        'enum',
        isEnumValue,
        (input, context) => (isEnumValue(input) ? t.success(input) : t.failure(input, context)),
        t.identity
    );
}

function optionalEnumCodec<T extends Record<string, string | number>>(e: T) {
    const isEnumValue = (input: any): input is T[keyof T] => Object.values(e).includes(input);
    return new t.Type<T[keyof T] | null | undefined, T[keyof T] | null | undefined, unknown>(
        'enum',
        (input): input is T[keyof T] | null | undefined => input === undefined || null || isEnumValue(input),
        (input, context) => (isEnumValue(input) || input === null ? t.success(input) : t.failure(input, context)),
        t.identity
    );
}

/**
 * Converts an enum to the key of the enum it matches to (e.g. 1 -> "one")
 * This is useful for converting enum values to strings for use in SQL queries for human readability
 * @example enum exampleEnum1 {
 *  ONE = 1,
 *  TWO = 2,
 * }
 * enumKeyCodec.decode(1); -> "ONE"
 * @example enum exampleEnum2 {
 * ONE = "one",
 * TWO = "two",
 * }
 * enumKeyCodec.decode("one"); -> "ONE"
 */
function enumToKeyStringCodec<T extends Record<string, string | number>>(e: T, options? : {
    upperCase?: boolean;
    lowerCase?: boolean;
    snakeCase?: boolean;
}) {
    const isEnumValue = (input: any): input is T[keyof T] => Object.values(e).includes(input);
    const getEnumKey = (value: T[keyof T]): keyof T => {
        const foundKey = Object.keys(e).find((key) => e[key] === value);
        return foundKey as keyof T;
    };
    return new t.Type<string, keyof T | null, unknown>(
        'enumToKeyString',
        isString,
        (input, context) => {
            if(isEnumValue(input)) {
                const key = getEnumKey(input);
                if(!key) return t.failure(input, context);
                if(options && options.snakeCase) {
                    return t.success(
                        options.upperCase ? pascalToSnake(key.toString(), 'upperCase') : 
                        options.lowerCase ? pascalToSnake(key.toString(), 'lowerCase') : 
                        pascalToSnake(key.toString())
                    );
                } else {
                    return t.success(
                        options && options.upperCase ? key.toString().toUpperCase() : 
                        options && options.lowerCase ? key.toString().toLowerCase() :
                        key.toString()
                    );
                }
            }
            return t.failure(input, context);
        },
        (v) => v
    );
}

const mixed = t.intersection;
const required = t.type;
const optional = t.partial;
const or = t.union;

export {
    decode, enumCodec, enumToKeyStringCodec, mixed, optional, optionalEnumCodec, or, required, t, timestampCodec
};

